<script lang="ts" setup>
import { computed } from 'vue';
import {
  ALLOWED_OUTREACH_RADIUS_MILES,
  V1SnapshotOutreachRadius,
} from '@factoryfixinc/ats-interfaces';
import {
  DEFAULT_OUTREACH_RADIUS_MILES,
  OUTREACH_RADIUS_HELPER_TEXT,
} from '@/core/jobs/copilot-activation/outreach-radius.constants';

const props = withDefaults(
  defineProps<{
    modelValue: V1SnapshotOutreachRadius | undefined | null;
    isDisabled?: boolean;
    title?: string;
    helperText?: string;
    // Drop the framing border/padding when the selector is rendered inside
    // another card (e.g. nested under the "+ passive candidates" option).
    embedded?: boolean;
  }>(),
  {
    isDisabled: false,
    title: 'Sourcing radius',
    helperText: OUTREACH_RADIUS_HELPER_TEXT,
    embedded: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: V1SnapshotOutreachRadius): void;
}>();

const selectedValue = computed<V1SnapshotOutreachRadius>(
  () => props.modelValue ?? DEFAULT_OUTREACH_RADIUS_MILES,
);

const handleSelect = (value: V1SnapshotOutreachRadius) => {
  if (props.isDisabled) return;
  if (value === selectedValue.value) return;
  emit('update:modelValue', value);
};

const labelFor = (value: V1SnapshotOutreachRadius) => `${value} miles`;
</script>

<template>
  <div
    data-test="sourcing-radius-selector"
    :class="[
      embedded ? '' : 'rounded-xl bg-highlight-0 px-6 py-4',
      { 'pointer-events-none opacity-60': isDisabled },
    ]"
  >
    <div
      :class="[
        'font-sans font-bold text-shade-900',
        embedded ? 'text-sm leading-[21px]' : 'text-base leading-6',
      ]"
    >
      {{ title }}
    </div>
    <div
      :class="[
        'font-sans text-sm font-normal text-shade-800',
        embedded ? 'mb-2 leading-[21px]' : 'mb-3 leading-5',
      ]"
    >
      {{ helperText }}
    </div>
    <div class="flex flex-wrap gap-2">
      <button
        v-for="option in ALLOWED_OUTREACH_RADIUS_MILES"
        :key="option"
        type="button"
        :data-test="`sourcing-radius-pill-${option}`"
        :aria-pressed="option === selectedValue"
        :disabled="isDisabled"
        class="pill"
        :class="{ 'pill--selected': option === selectedValue }"
        @click="handleSelect(option)"
      >
        {{ labelFor(option) }}
      </button>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.pill {
  @apply inline-flex h-8 w-20 items-center justify-center rounded-full border border-tint-100;
  @apply whitespace-nowrap bg-white px-3 font-sans text-sm font-normal leading-5 text-shade-820;
  @apply transition-colors;
}

.pill:hover:not(:disabled):not(.pill--selected) {
  @apply border-shade-840;
}

.pill--selected {
  @apply border-highlight-600 bg-highlight-50 font-bold text-highlight-600;
}

.pill:disabled {
  @apply cursor-not-allowed;
}
</style>
